import {Component, OnInit} from '@angular/core';
import {Constant} from '../../../global/util/Constant';
import HttpStatusCode from '../../../global/util/HttpStatusCode';
import {InformType} from '../../../global/util/enum/InformType';
import {AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService} from '../../../user/service/auth/auth.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ApplicationService} from '../../../user/service/application.service';
import {Person} from '../../../user/model/Person';
import {ProfileService} from '../../../user/service/profile.service';
import {Dialog} from '../../../global/util/Dialog';
import {TranslateService} from '@ngx-translate/core';
import {Passwordsecuritylevel} from '../../../user/model/Passwordsecuritylevel';
import {PasswordLevel} from '../../../global/util/PasswordLevel';

@Component({
  selector: 'app-recover-password-third-step',
  templateUrl: './recover-password-third-step.component.html',
  styleUrls: ['./recover-password-third-step.component.css']
})
export class RecoverPasswordThirdStepComponent implements OnInit {
  public title = 'TITLE.PASSWORD-UPDATE';
  public loading: boolean;
  public hash1: string;
  public hash2: string;
  public person = new Person();
  public personForm: FormGroup;
  public areSamePasswords = true;
  public minLengthPassword = 10;
  public maxLengthPassword = 14;
  public disablePassword = true;
  public fieldTextTypeNew: boolean;
  public fieldTextTypeNewRepeat: boolean;
  public readonly NEW_PASSWORD = 1;
  public readonly NEW_REPEAT_PASSWORD = 2;
  public ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  passwordSecurityLevel = new Passwordsecuritylevel();
  isSecurityHigh = false;
  isSecurityMedium = false;
  isSecurityLow = false;
  showConfirmPassword: boolean;
  username: any;

  constructor(private formBuilder: FormBuilder,
              private profileService: ProfileService,
              private router: Router,
              private authenticationService: AuthService,
              private modalService: NgbModal,
              private applicationService: ApplicationService,
              private activatedRoute: ActivatedRoute,
              private translate: TranslateService) {
    Dialog.createInstance(modalService);
    this.receiveParams();
    this.validateHash();
    this.createForm();
  }

  public receiveParams() {
    this.activatedRoute.queryParams
      .subscribe(params => {
        this.hash1 = params.hash1;
        this.hash2 = params.hash2;
        this.username = params.username;
      });
  }

  private validateHash() {
    this.loading = true;
    this.title = this.translate.instant('TITLE.CODE-VERIFICATION');
    const url = Constant.ROOT_API_V1.concat('/public/passwordrecovery/validate2?hash1=', this.hash1, '&hash2=', this.hash2);
    this.applicationService.post(url, new FormData()).subscribe(value => {
      this.loading = false;
    }, error => {
      this.loading = false;
      const status = error.status;
      if (status) {
        if (HttpStatusCode.NOT_FOUND === status) {
          Dialog.show(InformType.Info, this.title, error.error.message);
          this.router.navigate(['login']);
        } else {
          Dialog.show(InformType.Danger, this.title);
        }
      } else {
        Dialog.show(InformType.Danger, this.title, error.error.message);
      }
    });
  }

  public createForm() {
    this.personForm = this.formBuilder.group({
      newPassword: ['', [Validators.required, this.passwordComplexityMedium]],
      repeatNewPassword: ['', [Validators.required, this.passwordComplexityMedium]]
    }, {validator: this.passwordMatchValidator});
  }

  get passwordControls() {
    this.passwordSecurityLevel.level = this.passwordSecurityLevel.level !== null ? this.passwordSecurityLevel.level : 3;
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;
    switch (this.passwordSecurityLevel.level) {
      case PasswordLevel.PASSWORD_HIGH:
        this.isSecurityHigh = true;
        this.isSecurityMedium = false;
        this.isSecurityLow = false;
        return {
          hasUpperCase: /[A-Z]/.test(this.personForm.get('newPassword').value),
          hasLowerCase: /[a-z]/.test(this.personForm.get('newPassword').value),
          hasNumber: /[0-9]/.test(this.personForm.get('newPassword').value),
          hasSpecialChar: specialCharRegex.test(this.personForm.get('newPassword').value),
          hasNoSpaces: !/\s/.test(this.personForm.get('newPassword').value),
          isValidLength: this.personForm.get('newPassword').value?.length > 15
        };
      case PasswordLevel.PASSWORD_MEDIUM:
        this.isSecurityHigh = false;
        this.isSecurityMedium = true;
        this.isSecurityLow = false;
        return {
          hasUpperCase: /[A-Z]/.test(this.personForm.get('newPassword').value),
          hasLowerCase: /[a-z]/.test(this.personForm.get('newPassword').value),
          hasNumber: /[0-9]/.test(this.personForm.get('newPassword').value),
          hasNoSpaces: !/\s/.test(this.personForm.get('newPassword').value),
          isValidLength: this.personForm.get('newPassword').value?.length > 11
        };
      case PasswordLevel.PASSWORD_LOW:
        this.isSecurityHigh = false;
        this.isSecurityMedium = false;
        this.isSecurityLow = true;
        return {
          hasNoSpaces: !/\s/.test(this.personForm.get('newPassword').value),
          isValidLength: this.personForm.get('newPassword').value?.length > 7
        };
    }
    this.personForm.get('newPassword').updateValueAndValidity();
  }

  public save() {
    const newPasswordControl = this.personForm.get('newPassword');
    const confirmPasswordControl = this.personForm.get('repeatNewPassword');
    this.person.newPassword = newPasswordControl.value;
    this.person.repeatNewPassword = confirmPasswordControl.value;
    this.title = this.translate.instant('TITLE.PASSWORD-UPDATE');
    if (this.personForm.invalid || !this.areSamePasswords) {
      this.markAsTouchedAllControls();
      return;
    }
    const isFormValid = this.personForm.valid;
    const passwordRequirementsMet =
      this.isSecurityHigh ? (this.passwordControls.isValidLength &&
          this.passwordControls.hasUpperCase &&
          this.passwordControls.hasLowerCase &&
          this.passwordControls.hasNumber &&
          this.passwordControls.hasNoSpaces &&
          this.passwordControls.hasSpecialChar) :
        (this.isSecurityMedium ? (this.passwordControls.isValidLength &&
            this.passwordControls.hasUpperCase &&
            this.passwordControls.hasLowerCase &&
            this.passwordControls.hasNumber &&
            this.passwordControls.hasNoSpaces) :
          this.passwordControls.isValidLength && this.passwordControls.hasNoSpaces);

    if (isFormValid && passwordRequirementsMet) {
      this.setValuesBefore2Save();
      this.loading = true;
      const url = Constant.ROOT_API_V1.concat('/public/passwordrecovery/setup?hash1=', this.hash1, '&hash2=', this.hash2);
      const formData = new FormData();
      formData.append('newPassword', newPasswordControl.value);
      this.applicationService.post(url, formData).subscribe(value => {
        this.loading = false;
        localStorage.removeItem('uuid');
        Dialog.show(InformType.Success, this.title, this.translate.instant('MESSAGE.PASSWORD-RECOVERY-WAS-COMPLETED-SUCCESSFULLY'));
        this.router.navigate(['login']);
      }, error => {
        this.loading = false;
        this.processError(error);
      });
    } else {
      this.personForm.markAllAsTouched();
      this.handleFormErrors(newPasswordControl, confirmPasswordControl);
    }
  }

  private setValuesBefore2Save() {
    if (this.areSamePasswords) {
      this.person.newPassword = this.person.password;
    }
  }

  private markAsTouchedAllControls() {
    Object.values(this.personForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  public cancel() {
    this.router.navigate(['login']);
  }

  public isInvalidControl(name: string) {
    return this.personForm.get(name).invalid && this.personForm.get(name).touched;
  }

  public getErrorMessageBy(ctrl: string): string {
    const control = this.personForm.get(ctrl);
    const hasRequiredError = control.hasError('required');
    const hasPatternError = control.hasError('pattern');
    const whitespace = control.hasError('whitespace');
    switch (ctrl) {
      case 'newPassword':
      case 'repeatNewPassword':
        if (hasRequiredError) {
          return this.translate.instant('MESSAGE.PASSWORD-IS-REQUIRED');
        } else if (hasPatternError) {
          return this.translate.instant('MESSAGE.PASSWORD-NOT-COMPLY-WITH-REQUIRED-FORMAT');
        } else if (whitespace) {
          return String(this.translate.instant('MESSAGE.PASSWORD-DOES-NOT-CONTAIN-SPACE'));
        }
        break;
    }
  }

  public matchPasswords(event, otherControl: string) {
    const value: string = event.target.value;
    const otherValue: string = this.personForm.get(otherControl).value;
    if (value.length >= this.minLengthPassword && value.length <= this.maxLengthPassword && otherValue.length >= this.minLengthPassword && otherValue.length <= this.maxLengthPassword) {
      if (value === otherValue) {
        this.areSamePasswords = true;
      } else {
        this.areSamePasswords = false;
      }
    } else {
      this.areSamePasswords = false;
    }
  }

  private processError(error: any) {
    this.title = this.translate.instant('TITLE.PASSWORD-UPDATE');
    this.translate.get(error.error.message).subscribe(value => {
      error.error.message = value;
    });
    const status = error.status;
    if (status) {
      if (HttpStatusCode.NOT_FOUND === status) {
        Dialog.show(InformType.Info, this.title, error.error.message);
        this.router.navigate(['login']);
      } else {
        Dialog.show(InformType.Danger, this.title);
      }
    } else {
      Dialog.show(InformType.Danger, this.title, error.error.message);
    }
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  public toggleFieldTextType(password: number) {
    if (this.NEW_PASSWORD === password) {
      this.fieldTextTypeNew = !this.fieldTextTypeNew;
    } else if (this.NEW_REPEAT_PASSWORD === password) {
      this.fieldTextTypeNewRepeat = !this.fieldTextTypeNewRepeat;
    }
  }

  passwordComplexityMedium(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    if (!password) {
      return null;
    }
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = specialCharRegex.test(password);
    const hasNoSpaces = !/\s/.test(password);
    const valid = hasNoSpaces;
    return valid ? null : {passwordComplexity: true};
  }


  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPassword = group.get('newPassword').value;
    const confirmPassword = group.get('repeatNewPassword').value;
    return newPassword === confirmPassword ? null : {mismatch: true};
  }

  getHighPasswordLevel(username: any) {
    const url = Constant.ROOT_API_V1.concat('/public/level-security/').concat(username).concat('/password2');
    this.applicationService.get(url).subscribe((data: any) => {
      this.passwordSecurityLevel = data;
    }, error => {
      this.router.navigate(['login']).then();
    });
  }

  private showDanger(messageKey: string) {
    Dialog.show(InformType.Danger, this.translate.instant('TITLE.CHANGE-PASSWORD'), this.translate.instant(messageKey));
  }

  private handleFormErrors(newPasswordControl: any, confirmPasswordControl: any) {
    this.personForm.markAllAsTouched();
    if (newPasswordControl.invalid) {
      this.showDanger('MESSAGE.PASSWORD-REQUIRED-MUST-MEET-REQUIREMENTS');
    } else if (newPasswordControl.value !== confirmPasswordControl.value) {
      this.showDanger('MESSAGE.PASSWORDS-DO-NO-MATCH');
    } else {
      this.showDanger('MESSAGE.PASSWORD-REQUIRED-MUST-MEET-REQUIREMENTS');
    }
  }

  ngOnInit(): void {
    this.getHighPasswordLevel(this.username);
  }

  validatePassword(): void {
    const newPasswordControl = this.personForm.get('newPassword');
    const repeatNewPasswordControl = this.personForm.get('repeatNewPassword');
    if (newPasswordControl && newPasswordControl.value) {
      newPasswordControl.setValue(newPasswordControl.value.replace(/\s/g, ''));
    }
    if (repeatNewPasswordControl && repeatNewPasswordControl.value) {
      repeatNewPasswordControl.setValue(repeatNewPasswordControl.value.replace(/\s/g, ''));
    }
    if (newPasswordControl.value !== repeatNewPasswordControl.value) {
      this.areSamePasswords = false;
    } else {
      this.areSamePasswords = true;
    }
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isWhitespace = (control.value || '').trim().length !== control.value.length;
      const hasInnerSpaces = (control.value || '').includes(' ');
      const isValid = !isWhitespace && !hasInnerSpaces;
      return isValid ? null : {whitespace: true};
    };
  }
}
